import { Box, Text, VStack, HStack, Button, Spinner, Badge } from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogCloseTrigger,
} from '@/components/ui/dialog';
import { useEffect } from 'react';
import { FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import { useAIProposalStore } from '@/stores/aiProposalStore';
import { AIMatingProposalCard } from './AIMatingProposalCard';

interface AIMatingProposalModalProps {
  /** モーダルの開閉状態 */
  open: boolean;
  /** モーダルを閉じるコールバック */
  onClose: () => void;
  /** 対象メス犬のID */
  femaleId: string;
  /** 対象メス犬の名前 */
  femaleName: string;
  /** ユーザーID */
  userId: string;
}

/**
 * AI交配提案モーダルコンポーネント
 */
export function AIMatingProposalModal({
  open,
  onClose,
  femaleId,
  femaleName,
  userId,
}: AIMatingProposalModalProps) {
  const {
    proposals,
    loading,
    error,
    disclaimer,
    fetchProposals,
    clearProposals,
  } = useAIProposalStore();

  // モーダルが開いたらデータを取得
  useEffect(() => {
    if (open && femaleId && userId) {
      fetchProposals(femaleId, userId);
    }
  }, [open, femaleId, userId, fetchProposals]);

  // モーダルが閉じたらデータをクリア
  const handleClose = () => {
    clearProposals();
    onClose();
  };

  // 再分析
  const handleRefresh = () => {
    fetchProposals(femaleId, userId);
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="xl"
    >
      <DialogContent maxHeight="90vh" overflow="hidden" display="flex" flexDirection="column">
        <DialogHeader>
          <DialogTitle>
            <HStack gap={2}>
              <Text>AI交配提案</Text>
              <Badge colorPalette="pink" size="sm">
                {femaleName}
              </Badge>
            </HStack>
          </DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>

        <DialogBody flex="1" overflow="auto" py={4}>
          {/* ローディング状態 */}
          {loading && (
            <VStack py={12} gap={4}>
              <Spinner size="xl" color="blue.500" />
              <Text color="gray.600">AI分析中...</Text>
              <Text fontSize="sm" color="gray.500">
                候補オス犬の評価を行っています。しばらくお待ちください。
              </Text>
            </VStack>
          )}

          {/* エラー状態 */}
          {error && !loading && (
            <VStack py={8} gap={4}>
              <Box
                p={4}
                bg="red.50"
                borderRadius="md"
                borderLeft="4px solid"
                borderLeftColor="red.500"
                width="100%"
              >
                <HStack gap={2} mb={2}>
                  <FiAlertTriangle color="var(--chakra-colors-red-500)" />
                  <Text fontWeight="medium" color="red.700">
                    エラーが発生しました
                  </Text>
                </HStack>
                <Text fontSize="sm" color="red.600">
                  {error}
                </Text>
              </Box>
              <Button onClick={handleRefresh} size="sm">
                <HStack gap={1}>
                  <FiRefreshCw />
                  <Text>再試行</Text>
                </HStack>
              </Button>
            </VStack>
          )}

          {/* 提案リスト */}
          {!loading && !error && proposals.length > 0 && (
            <VStack gap={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                {femaleName}に最適なオス犬を{proposals.length}頭提案します
              </Text>

              {proposals.map((proposal, index) => (
                <AIMatingProposalCard
                  key={proposal.maleId}
                  proposal={proposal}
                  rank={index + 1}
                  femaleId={femaleId}
                />
              ))}
            </VStack>
          )}

          {/* 候補なし */}
          {!loading && !error && proposals.length === 0 && (
            <VStack py={8} gap={4}>
              <Text color="gray.600">提案可能なオス犬が見つかりませんでした</Text>
              <Text fontSize="sm" color="gray.500">
                アクティブなオス犬が登録されていることを確認してください
              </Text>
            </VStack>
          )}
        </DialogBody>

        <DialogFooter borderTop="1px solid" borderTopColor="gray.100">
          <VStack width="100%" gap={3}>
            {/* 免責事項 */}
            {disclaimer && (
              <Box
                p={3}
                bg="gray.50"
                borderRadius="md"
                width="100%"
              >
                <Text fontSize="xs" color="gray.500">
                  {disclaimer}
                </Text>
              </Box>
            )}

            <HStack width="100%" justify="space-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
              >
                <HStack gap={1}>
                  <FiRefreshCw />
                  <Text>再分析</Text>
                </HStack>
              </Button>
              <Button onClick={handleClose}>閉じる</Button>
            </HStack>
          </VStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
